import { useEffect } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Button } from "@/components/ui/Button";
import { WorkspaceMembersSection } from "@/components/settings/WorkspaceMembersSection";
import { NotificationPrefsSection } from "@/components/settings/NotificationPrefsSection";
import { AdminNotificationDefaults } from "@/components/settings/AdminNotificationDefaults";
import { BackupStatusSection } from "@/components/settings/BackupStatusSection";
import { useAuth } from "@/hooks/useAuth";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useDateFormat } from "@/hooks/useDateFormat";
import { useTheme } from "@/hooks/useTheme";
import { BRAND } from "@/lib/tokens";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";

export function SettingsPage() {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [dateFormat, setDateFormat] = useDateFormat();
  const isDesktop = useBreakpoint("md");
  const isAdmin = user?.roles.includes("flow-admin") ?? false;

  // Deep-link from the email footer (/settings#notifications) scrolls to the
  // matching section. Runs after mount so the section is in the DOM.
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div
      style={{
        flex: 1,
        overflow: "auto",
        padding: isDesktop ? "28px 36px 80px" : "16px 12px 80px",
        background: "var(--bg)",
      }}
    >
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <div style={{ marginBottom: 10 }}>
          <Breadcrumb items={[{ label: t.navHome, to: "/" }, { label: t.navSettings }]} />
        </div>

        <h1
          style={{
            margin: "0 0 26px",
            fontSize: 24,
            fontWeight: 600,
            color: "var(--ink-1)",
            letterSpacing: "-0.015em",
          }}
        >
          {t.navSettings}
        </h1>

        <Section title={t.settingsProfileTitle} description={t.settingsProfileDescription}>
          {user ? (
            <div style={fieldRowStyle}>
              <Field label={t.settingsName} value={user.name ?? "—"} />
              <Field label={t.settingsEmail} value={user.email ?? "—"} />
              <Field
                label={t.settingsRoles}
                value={user.roles.length ? user.roles.join(", ") : "—"}
              />
              <Field label={t.settingsKeycloakSub} value={user.sub} mono />
            </div>
          ) : (
            <div style={{ color: "var(--ink-3)", fontSize: 13 }}>{t.loading}</div>
          )}
        </Section>

        <Section title={t.settingsThemeTitle} description={t.settingsThemeDescription}>
          <div style={{ display: "flex", gap: 8 }}>
            <ThemeChoice
              label={t.themeLight}
              icon="☀"
              active={theme === "light"}
              onClick={() => setTheme("light")}
            />
            <ThemeChoice
              label={t.themeDark}
              icon="☾"
              active={theme === "dark"}
              onClick={() => setTheme("dark")}
            />
          </div>
          <div style={{ marginTop: 18 }}>
            <h3 style={{ fontSize: 13, fontWeight: 650, color: "var(--ink-1)", margin: "0 0 2px" }}>
              {t.dateFormatHeading}
            </h3>
            <p style={{ margin: "0 0 10px", fontSize: 12.5, color: "var(--ink-3)" }}>
              {t.dateFormatDescription}
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <ThemeChoice
                label={t.dateFormatRelative}
                active={dateFormat === "relative"}
                onClick={() => setDateFormat("relative")}
              />
              <ThemeChoice
                label={t.dateFormatAbsolute}
                active={dateFormat === "absolute"}
                onClick={() => setDateFormat("absolute")}
              />
            </div>
          </div>
        </Section>

        <Section title={t.membersSectionTitle} description={t.membersSectionDescription}>
          <WorkspaceMembersSection />
        </Section>

        <Section
          id="notifications"
          title={t.settingsNotificationsTitle}
          description={t.settingsNotificationsDescription}
        >
          <NotificationPrefsSection />
        </Section>

        {isAdmin ? (
          <Section title={t.settingsAdminTitle} description={t.settingsAdminDescription}>
            <div style={{ marginBottom: 20 }}>
              <h3
                style={{
                  fontSize: 13,
                  fontWeight: 650,
                  color: "var(--ink-1)",
                  margin: "0 0 4px",
                }}
              >
                {t.adminNotifDefaultsHeading}
              </h3>
              <AdminNotificationDefaults />
            </div>
            <div id="backups" style={{ marginBottom: 20 }}>
              <h3
                style={{
                  fontSize: 13,
                  fontWeight: 650,
                  color: "var(--ink-1)",
                  margin: "0 0 4px",
                }}
              >
                {t.backupHeading}
              </h3>
              <BackupStatusSection />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Button
                variant="secondary"
                size="md"
                icon="person"
                onClick={() => showToast(t.comingSoon)}
              >
                {t.settingsAdminUsers}
              </Button>
              <Button
                variant="secondary"
                size="md"
                icon="layers"
                onClick={() => showToast(t.comingSoon)}
              >
                {t.settingsAdminWorkspaceMgmt}
              </Button>
            </div>
          </Section>
        ) : null}
      </div>
    </div>
  );
}

const sectionStyle: CSSProperties = {
  background: "var(--surface)",
  borderRadius: 10,
  border: "1px solid var(--hairline)",
  padding: "18px 20px 20px",
  marginBottom: 16,
};

function Section({
  title,
  description,
  children,
  id,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  id?: string;
}) {
  return (
    <div id={id} style={sectionStyle}>
      <h2
        style={{
          margin: 0,
          fontSize: 14,
          fontWeight: 600,
          color: "var(--ink-1)",
          letterSpacing: "-0.005em",
        }}
      >
        {title}
      </h2>
      {description ? (
        <p style={{ margin: "4px 0 14px", fontSize: 12.5, color: "var(--ink-3)" }}>{description}</p>
      ) : (
        <div style={{ height: 14 }} />
      )}
      {children}
    </div>
  );
}

const fieldRowStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 14,
};

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "var(--ink-3)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13.5,
          color: "var(--ink-1)",
          fontFamily: mono
            ? "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace"
            : "Inter Variable, Inter, system-ui, sans-serif",
          wordBreak: "break-all",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function ThemeChoice({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 14px",
        borderRadius: 8,
        background: active ? "var(--surface-elev)" : "var(--surface)",
        border: `1px solid ${active ? BRAND.blue : "var(--hairline-strong)"}`,
        color: "var(--ink-1)",
        cursor: "pointer",
        fontSize: 13.5,
        fontWeight: active ? 600 : 500,
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        transition: "background .12s, border-color .12s",
      }}
    >
      {icon ? <span style={{ fontSize: 16, lineHeight: 1 }}>{icon}</span> : null}
      {label}
    </button>
  );
}
